import { Suspense } from 'react'
import { Link } from 'react-router'
import { IconChevronRight, IconSpeakerphone } from '@tabler/icons-react'
import { Typography } from '../common/typography'
import { News, useSingleNews } from '../news/api'
import {
  Image,
  Modal,
  Text,
  Skeleton,
  FocusTrap,
  AspectRatio,
  Drawer,
  DrawerHeader,
  DrawerCloseButton,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import DOMPurify from 'dompurify'
import classes from './Announcement.module.css'
import clsx from 'clsx'

export default function Announcement({ announcement }: { announcement: News }) {
  const [opened, { open, close }] = useDisclosure(false)
  const isDesktop = useMediaQuery('(min-width: 800px)')

  return (
    <div className={classes.announcementWrapper}>
      <div className={classes.announcementRow}>
        <Typography fs="lg" fw={500} lh="100">
          Announcement
        </Typography>
        <Link
          to="/news"
          state={{ category: 'announcements' }}
          className={classes.link}
        >
          See All
          <IconChevronRight className={classes.linkIcon} />
        </Link>
      </div>
      <div className={classes.announcementCard} onClick={open}>
        {announcement.image ? (
          <AspectRatio ratio={16 / 9} className={classes.announcementImage}>
            <Image
              src={announcement.image.main}
              alt={announcement.title}
              h="100%"
            />
          </AspectRatio>
        ) : (
          <div className={classes.announcementCover}>
            <IconSpeakerphone className={classes.announcementIcon} />
          </div>
        )}
        <div
          className={clsx(classes.announcementInfo, {
            [classes.announcementInfoMobile]: !isDesktop,
          })}
        >
          <Typography
            fs={isDesktop ? 'xl' : 'md'}
            fw={isDesktop ? 700 : 500}
            lh="100"
            className={classes.title}
          >
            {announcement.title}
          </Typography>
          <Typography fs="xs" fc={{ light: 'gray-5', dark: 'gray-5' }} lh="150">
            {new Date(announcement.created_at).toLocaleDateString('en-GB')}
          </Typography>
          {isDesktop && (
            <Typography lh="150" className={classes.summary}>
              {announcement.summary}
            </Typography>
          )}
          <Typography
            fs={isDesktop ? 'md' : 'sm'}
            fc={{ light: 'primary-5', dark: 'primary-3' }}
            lh="150"
            className={classes.readMore}
          >
            Read more
          </Typography>
          <div className={classes.cardDetails} />
        </div>
      </div>
      {isDesktop ? (
        <Modal.Root
          opened={opened}
          onClose={close}
          centered
          size="lg"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{ content: classes.modalContent }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <FocusTrap.InitialFocus />
            <Modal.Header>
              <Modal.Title>
                <Typography fw={500} lh="125">
                  {announcement.title}
                </Typography>
                <Typography
                  fs="xs"
                  fc={{ light: 'gray-5', dark: 'gray-5' }}
                  lh="150"
                >
                  {new Date(announcement.created_at).toLocaleDateString(
                    'en-GB'
                  )}
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              {announcement.news_id && (
                <Suspense
                  fallback={
                    <>
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                    </>
                  }
                >
                  <AnnouncementBody news_id={announcement.news_id} />
                </Suspense>
              )}
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={opened}
          onClose={close}
          position="bottom"
          classNames={{
            content: classes.drawerContent,
            header: classes.drawerHeader,
            body: classes.drawerBody,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <FocusTrap.InitialFocus />
            <DrawerHeader>
              <div className={classes.drawerHeaderWrapper}>
                <Typography fw={500} lh="125">
                  {announcement.title}
                </Typography>
                <Typography
                  fs="xs"
                  fc={{ light: 'gray-5', dark: 'gray-5' }}
                  lh="150"
                >
                  {new Date(announcement.created_at).toLocaleDateString(
                    'en-GB'
                  )}
                </Typography>
              </div>
              <DrawerCloseButton />
            </DrawerHeader>
            <Drawer.Body>
              {announcement.news_id && (
                <Suspense
                  fallback={
                    <>
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                      <Skeleton height={16} radius="sm" mb="sm" />
                    </>
                  }
                >
                  <AnnouncementBody news_id={announcement.news_id} />
                </Suspense>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  )
}

function AnnouncementBody({ news_id }: { news_id: number }) {
  const { data: news } = useSingleNews({ news_id })

  return (
    <Text
      component="p"
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(news.body || ''),
      }}
    />
  )
}
